/**
 * firmaPersonelListService.js — Firma tarafı "Personel Listesi" (kart görünümü)
 * Route: /Firma/Personel (liste) + /Firma/Personel/:personelId (detay)
 *
 * Ana Sayfa'daki (FirmaAnasayfaListPage.jsx) "Kurulum ve Söküm Kartı Durumu"
 * panelinin "Tüm Personeller" bağlantısının hedefi. firmaProfileService.js'teki
 * tek kaynak personel kadrosunu (getFirmaPersonnel) ve firma çapındaki 3 belgeyi
 * (getFirmaBelgeleri) arama/sayfalama + kart görünümü için gereken sayaçlarla
 * (Bekliyor/Onaylı/Reddedildi) süsler — ayrı bir mock veri seti yok, bkz. aynı
 * desen firmaTalepListService.js / firmaProjeListService.js.
 *
 * Standlarım/Talepler/Projeler'in aksine fuar'a değil sadece firmaya bağlıdır
 * (personel kadrosu hangi fuarın seçili olduğundan bağımsız) — bu yüzden mock
 * modda liste sayfası selectedFuarId ile filtrelemez.
 *
 * 2026-08-11 revizyonu (mock→backend reconciliation, bkz.
 * docs/guides/FE-PersonnelController-guide.md): canlı dal artık gerçek
 * `GET /personnel/firma/{firmaId}` / `GET /personnel/firma/{firmaId}/{personId}`
 * uç noktalarını çağırıyor. Rehberin aksine, backend'de kadro GERÇEKTEN
 * fuar-bazlıdır (`fuarId` zorunlu query param — "hired workers are per-fair" —
 * dolayısıyla FirmaPersonelListPage.jsx/FirmaPersonelDetailPage.jsx artık diğer
 * Firma sayfalarıyla (FirmaTaleplerListPage.jsx vb.) aynı selectedFuarId/
 * LIVE_TEST_FUAR_IDS köprüsünü kullanıyor — bu dosyanın kendisi mock dalında
 * hâlâ fuar'dan bağımsız (üstteki not değişmedi). Test verisi: fuar 298,
 * decoratorFirmaId 49858 (Örnek Yapı A.Ş.) — 4 kişi seed edildi (isim soyadına
 * " (t)" işaretiyle), bkz. PersonnelController guide'ın "Known-good test data"
 * bölümü ve local-backend-testing.md.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { getFirmaPersonnel, getFirmaBelgeleri, firmaBelgeleriAggregateStatus } from './firmaProfileService';
import { answerEslesme, ESLESME_DURUM } from './personelRosterService';
import i18n from "../i18n";

export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

// FirmaTaleplerListPage.jsx'teki aynı isimli sabitin dosya-yerel kopyası (bkz.
// o dosyadaki 2026-08-10 notu / util/knowledgebase/local-backend-testing.md) —
// giriş yapan hesabın kendi firmaId'si backend'den henüz dönmüyor.
export const TEST_DECORATOR_FIRMA_ID = 49858;

const PERSONEL_BELGE_KEYS = ['sgk', 'isg', 'saglik', 'meslekiEgitim'];

// Firma çapında tek kayıt olan 3 belge — Firma İSG Belgeleri grubu (bkz. #156, FirmaKartPage.jsx).
const FIRMA_BELGE_KEYS = ['firmaIsgTaahhutnamesi', 'isYeriHekimiAtamasi', 'isGuvenligiUzmaniAtamasi'];

/**
 * Canlı FirmaPersonelDTO'nun `firmaBelgeleri`si DÜZ ve `Status` ekiyle gelir
 * ({firmaIsgTaahhutnamesiStatus: 1, ...} — bkz. FE-PersonnelController-guide.md),
 * mock ise firmaProfileService'in {anahtar: {status}} şeklini kullanır. Aynı çeviri
 * getFirmaBelgeleri'nin canlı dalında da yapılıyor ("sayfa değişmesin" diye) —
 * burada da yapılmazsa aynı sayfa canlıda ve mock'ta farklı bir şekil okur.
 * Not: `firmaBelgeleriStatus` (üçünün toplamı) DTO'da hazır geliyor, o çevrilmez.
 */
function mapFirmaBelgeleri(raw) {
  if (!raw) return {};
  return Object.fromEntries(
    FIRMA_BELGE_KEYS.map((k) => [k, { status: raw[`${k}Status`] ?? null }]),
  );
}

// Backend 400/500'ü bazen düz metin, bazen {message,...} zarfı döner (bkz.
// rehber "Error bodies are inconsistent") — projectService.js/standTalepService.js'teki
// aynı yardımcının kopyası.
function parseError(error) {
  const message =
    typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.message ?? error.message ?? i18n.t("services:firmaPersonelListService.bilinmeyenHata");
  throw new Error(message);
}

/**
 * Kadro satırının "Saka eşlemesine firmanın cevabı" alanları (backend #113). Canlı DTO ile aynı
 * adlar; `eslesmeDurumu` null = 2026-09-03 öncesi görevlendirme, kaydı yok (Onaylandı DEĞİL).
 * `eslesmeRedSebebi` ve `eslesmeGecmisi` taşeron-delege okumada backend'den null gelir.
 */
function mapEslesme(dto) {
  return {
    eslesmeTipi: dto.eslesmeTipi ?? null,
    eslesmeTipiAdi: dto.eslesmeTipiAdi ?? null,
    eslesmeTarihi: dto.eslesmeTarihi ?? null,
    eslesenAdi: dto.eslesenAdi ?? null,
    eslesmeDurumu: dto.eslesmeDurumu ?? null,
    eslesmeDurumuAdi: dto.eslesmeDurumuAdi ?? null,
    eslesmeKararTarihi: dto.eslesmeKararTarihi ?? null,
    eslesmeKararVerenAdi: dto.eslesmeKararVerenAdi ?? null,
    eslesmeRedSebebi: dto.eslesmeRedSebebi ?? null,
    eslesmeGecmisi: dto.eslesmeGecmisi ?? null,
  };
}

/**
 * Firmanın bu kişiyle ilişkisinin bittiği bilgisi (person_to_firma, backend #112/#65). Tek kişi
 * görünümünde hep gelir; kadro listesinde yalnızca fuar CANLIYKEN (aksi halde bitmiş kişiler listeden
 * düşer). `false` = aktif satır YA DA roster satırı olmayan eski görevlendirme (yazma serbest).
 * Bitmiş ilişkide okuma sürer ama yazma uçları (dosya yükle/sil, taşıma) 403 verir. Alan adları
 * `iliskiBitisMeta`nın okuduklarıyla aynı, bu yüzden `IliskiBitisRozeti` doğrudan çalışır.
 */
function mapIliskiBitis(dto) {
  return {
    iliskiSonlandirildi: dto.iliskiSonlandirildi === true,
    iliskiBitisTarihi: dto.iliskiBitisTarihi ?? null,
    iliskiBitirenAdi: dto.iliskiBitirenAdi ?? null,
    iliskiBitisKaynagi: dto.iliskiBitisKaynagi ?? null,
    iliskiBitisKaynagiAdi: dto.iliskiBitisKaynagiAdi ?? null,
    iliskiBitisSebebi: dto.iliskiBitisSebebi ?? null,
  };
}

// Mock kadro: ilk dört kişi birer eşleme senaryosu taşır (Bekliyor / Onaylandı / Reddedildi /
// firmanın kendi Görevlendirme'si), kalanı kaydı olmayan eski görevlendirme. Firmanın mock'ta
// verdiği cevaplar oturum boyunca burada durur.
const MOCK_ESLESME_SENARYOLARI = [
  { eslesmeTipi: 2, eslesmeDurumu: 0 },
  { eslesmeTipi: 2, eslesmeDurumu: 1, eslesmeKararTarihi: '2026-09-20 10:15:00' },
  {
    eslesmeTipi: 2, eslesmeDurumu: 2, eslesmeKararTarihi: '2026-09-21 09:40:00',
    eslesmeRedSebebi: 'Bu kişi firmamızda çalışmıyor.',
  },
  { eslesmeTipi: 1, eslesmeDurumu: 1 },
];
const mockEslesmeCevaplari = new Map(); // personId → { durum, sebep, tarih }

function nowStamp() {
  return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

function mockEslesme(person, index) {
  const senaryo = MOCK_ESLESME_SENARYOLARI[index];
  if (!senaryo) return mapEslesme({});
  const cevap = mockEslesmeCevaplari.get(person.id);
  const base = {
    ...senaryo,
    eslesmeTipiAdi: senaryo.eslesmeTipi === 2 ? 'Eşleme' : 'Görevlendirme',
    eslesmeTarihi: '2026-09-18 14:00:00',
    eslesenAdi: senaryo.eslesmeTipi === 2 ? 'Deniz Keskin' : person.name,
    eslesmeKararVerenAdi: senaryo.eslesmeDurumu === 0 || senaryo.eslesmeTipi === 1 ? null : 'Firma Yetkilisi',
    eslesmeGecmisi: [],
  };
  if (cevap && senaryo.eslesmeTipi === 2) {
    base.eslesmeDurumu = cevap.durum;
    base.eslesmeKararTarihi = cevap.tarih;
    base.eslesmeKararVerenAdi = 'Firma Yetkilisi';
    base.eslesmeRedSebebi = cevap.durum === 2 ? cevap.sebep : null;
  }
  base.eslesmeDurumuAdi = ESLESME_DURUM[base.eslesmeDurumu]?.label ?? null;
  return mapEslesme(base);
}

function eslesmeSayilari(records) {
  const sayi = { bekliyor: 0, onaylandi: 0, reddedildi: 0, kayitYok: 0 };
  records.forEach((r) => {
    if (r.eslesmeDurumu === 0) sayi.bekliyor += 1;
    else if (r.eslesmeDurumu === 1) sayi.onaylandi += 1;
    else if (r.eslesmeDurumu === 2) sayi.reddedildi += 1;
    else sayi.kayitYok += 1;
  });
  return sayi;
}

// GET /personnel/firma/{firmaId}[/{personId}]'nin FirmaPersonelDTO'sunu bu
// sayfaların beklediği mock alan adlarına çevirir — documents{}/firmaBelgeleri/
// firmaBelgeleriStatus/muaf/statusCounts/cardEligible zaten aynı şekilde geliyor,
// sadece kimlik alanları (personId→id, name+surname, tcNoMasked→maskedTcNo,
// owner→role) yeniden adlandırılıyor.
function mapPersonnelRecord(dto) {
  const fullName = [dto.name, dto.surname].filter(Boolean).join(' ').trim();
  return {
    id: dto.personId,
    name: fullName || '—',
    maskedTcNo: dto.tcNoMasked,
    // #293 (backend #270): tam numara; taşeron devriyle okunan satırda null → ekranlar `maskedTcNo`ya düşer.
    tcNo: dto.tcNo ?? null,
    // `logo` salt bir dosya adı. 2026-09-03'e kadar bir URL'e çevrilemiyordu (kişi
    // dosyaları için whitelist'li serve path YOK ve olmayacak da) — artık kimlik
    // doğrulamalı `GET /personnel/{personId}/fotograf` var, ama o bir <img src> ile
    // değil blob ile alınıyor. Bu yüzden burada hâlâ null: sayfa fotoğrafı
    // `personelRosterService.fetchKisiFotograf` ile ayrıca çeker ve LEGACY 404'ü
    // (bugün 31.638 vesikalığın 31.638'i) varsayılan görünüm olarak ele alır.
    // `hasPhoto` yalnızca "denemeye değer mi" sorusunu cevaplar.
    photo: null,
    hasPhoto: !!dto.logo,
    // roster DTO'sunda karşılığı yok (bkz. rehberin "Asked for but not built"
    // tablosu) — sayfa null'ı zaten "—" ile gösteriyor.
    uyruk: null,
    phone: dto.phone,
    role: dto.owner ? 'isveren' : 'isci',
    forbidden: dto.forbidden,
    documents: dto.documents,
    firmaBelgeleri: mapFirmaBelgeleri(dto.firmaBelgeleri),
    // #258 — firmanın belge grupları (kod: FIRMA | ISG); Firma İSG Belgeleri kutucukları sabit üç anahtar
    // yerine ISG grubundan çizilir, böylece İşveren görevlendirmesi (99) da görünür. Mock satırda yok.
    firmaBelgeGruplari: dto.firmaBelgeGruplari ?? null,
    firmaBelgeleriStatus: dto.firmaBelgeleriStatus,
    // ⚠️ `muaf` ARTIK KİMSEYİ MUAF TUTMUYOR (2026-08-28) — alan adı bir yalan, geriye
    // dönük uyumluluk için duruyor. Doğru alan `firmaBelgeleriTamam`; firmanın üç İSG
    // belgesi artık bir MUAFİYET değil bir ŞART. Rozeti "Muaf" diye etiketlemeyin.
    muaf: dto.muaf,
    firmaBelgeleriTamam: dto.firmaBelgeleriTamam,
    // ⚠️ Denominatör DEĞİŞTİ: artık sabit dörtlü değil, `1 (KVKK) + mesleğin istediği
    // belgeler`. "approved / 4" yazan bir arayüz yanlış — `gerekliBelgeler.length` kullanın.
    statusCounts: dto.statusCounts,
    cardEligible: dto.cardEligible,
    // Üç durumlu rozet (2026-09-15) — bkz. kartDurumuService.js.
    kartDurumu: dto.kartDurumu ?? null,
    // "Neden giremiyor" — backend hazır veriyor, kuralı JS'te tekrar kurmak tam olarak
    // sayfaların yalan söylemesine sebep olan şeydi.
    uygunsuzluklar: dto.uygunsuzluklar || [],
    cardEligibleReason: dto.cardEligibleReason ?? null,
    // Kararın geldiği yol: CALISAN | ISVEREN (backend #125) — bkz. kartDurumuService.isverenYolu.
    kartYolu: dto.kartYolu ?? null,
    gerekliBelgeler: dto.gerekliBelgeler || [],
    belgeKurali: dto.belgeKurali ?? null,
    meslekId: dto.meslekId ?? null,
    meslekName: dto.meslekName ?? null,
    meslekKaynak: dto.meslekKaynak ?? null,
    // #372 (backend #371, FUAR BAŞINA): kişinin bu fuardaki TÜM meslekleri (her firmanın verdiği; `buFirmada` bu firmanın
    // verdiği), her mesleğin Mesleki Eğitim yuvası ve hiçbir mesleğe sayılmayan etiketsiz sertifikalar. Fuarsız/yalın
    // satırda üçü de null — `documents.meslekiEgitim` o zaman eskisi gibi tek sertifika.
    meslekler: dto.meslekler ?? null,
    meslekiEgitimler: dto.meslekiEgitimler ?? null,
    etiketsizMeslekiEgitimBelgeleri: dto.etiketsizMeslekiEgitimBelgeleri ?? null,
    // #466 (backend #464): `meslekler[]` artık KİŞİNİN meslekleri (fuardan ve firmadan bağımsız; her biri kimin eklediği, `onayli`
    // ve kategorisiyle), `kartMeslekleri[]` kartın "Çalışan" dışında hangi meslekle basılabileceği, `kartCalisanBasilabilir` kartın
    // Çalışan olarak basılabildiği. Kartın bir meslekle basılabilmesi kararı backend'de; burada yalnızca taşınır.
    kartMeslekleri: dto.kartMeslekleri ?? null,
    kartCalisanBasilabilir: dto.kartCalisanBasilabilir ?? null,
    kimlikDogrulama: dto.kimlikDogrulama ?? null,
    kimlikDogrulamaZorunlu: !!dto.kimlikDogrulamaZorunlu,
    personKvkkStatusId: dto.personKvkkStatusId ?? null,
    // KVKK'nın üçüncü kaynağı (backend #139): kişinin kendi dijital onayı. `tamam` ise belge
    // olmasa da giriş kuralı karşılanır — KVKK karesi yeşil okunur (backlog #252).
    kvkkDijitalOnay: dto.kvkkDijitalOnay ?? null,
    // Fuar başına verilen Özel İzin. ⚠️ `typeId` yalnızca İNİSİYATİFİ kaydeder, yetki
    // sıralaması değildir — kuralı açan şey nesnenin VAR OLMASI. `label`ı basın.
    singlePermit: dto.singlePermit ?? null,
    // #374 (backend #373): KKD at THIS fair — `{ fuarId, istenen, durum UYGUN|UYARI|EKSIK|ISTENMIYOR, uygun, kalemler[] }`, only the items the
    // fair asks for. Without it every company-side list (Yerli Katılımcı, Dekoratör, Yabancı, staff Firma Detay) drew a flat gray KKD square and
    // an unmet blocking KKD never turned the card decision yellow. null = no fair in the request (or a mock row).
    kkd: dto.kkd ?? null,
    bilgiler: dto.bilgiler ?? null,
    ...mapEslesme(dto),
    ...mapIliskiBitis(dto),
  };
}

function enrichPersonel(person, firmaBelgeleri) {
  const firmaBelgeleriStatus = firmaBelgeleriAggregateStatus(firmaBelgeleri);
  const muaf = firmaBelgeleriStatus === 1;

  let pending = 0;
  let approved = 0;
  let rejected = 0;
  const tally = (status) => {
    if (status === 0) pending += 1;
    else if (status === 1) approved += 1;
    else if (status === 2) rejected += 1;
  };
  tally(person.documents.kvkk.status);
  if (!muaf) PERSONEL_BELGE_KEYS.forEach((k) => tally(person.documents[k].status));

  return {
    ...person,
    firmaBelgeleri,
    firmaBelgeleriStatus,
    muaf,
    statusCounts: { pending, approved, rejected },
  };
}

/**
 * Bu dekoratör firmanın Kurulum/Söküm kartı alabilecek personel kadrosu —
 * arama (ad soyad) + sayfalama. Canlıda `fuarId` ZORUNLU (bkz. dosya başı
 * 2026-08-11 notu) — mock dalı geriye dönük uyumluluk için hâlâ görmezden gelir.
 *
 * `eslesmeDurumu` (0/1/2, backend #113) firmanın Saka eşlemesine verdiği cevaba göre süzer;
 * verilmezse hepsi (kaydı olmayan eski satırlar yalnızca o zaman çıkar). Dönüşteki
 * `eslesmeDurumSayilari` süzgeçten etkilenmez — sekme rozetleri bundan.
 * GET /api/v1/personnel/firma/{firmaId}?fuarId=&search=&eslesmeDurumu=&page=&size= (0-tabanlı)
 */
export async function getFirmaPersonelListesi(params = {}, { forceLive = false } = {}) {
  const { decoratorFirmaId, fuarId, search = '', page = 1, limit = 10, eslesmeDurumu } = params;

  if (USE_MOCK_DATA && !forceLive) {
    const [personnel, firmaBelgeleri] = await Promise.all([
      getFirmaPersonnel(decoratorFirmaId),
      getFirmaBelgeleri(decoratorFirmaId),
    ]);

    // Senaryo kişiye INDEX'iyle bağlı — arama/süzgeç sonrası sıra kaymasın diye süzmeden önce.
    const eslesmeli = personnel.map((p, i) => ({ ...p, ...mockEslesme(p, i) }));

    const query = search.trim().toLowerCase();
    const filtered = query ? eslesmeli.filter((p) => p.name.toLowerCase().includes(query)) : eslesmeli;
    const sayilar = eslesmeSayilari(filtered);
    const gorunen = eslesmeDurumu === undefined || eslesmeDurumu === null
      ? filtered
      : filtered.filter((p) => p.eslesmeDurumu === eslesmeDurumu);

    const total = gorunen.length;
    const start = (page - 1) * limit;
    const records = gorunen.slice(start, start + limit).map((p) => enrichPersonel(p, firmaBelgeleri));

    return { total, page, limit, records, eslesmeDurumSayilari: sayilar };
  }

  try {
    const query = { fuarId, search, page: page - 1, size: limit };
    if (eslesmeDurumu !== undefined && eslesmeDurumu !== null) query.eslesmeDurumu = eslesmeDurumu;
    const response = await axios.get(`${JAVA_BACKEND_API_URL}/personnel/firma/${decoratorFirmaId}`, {
      params: query,
      headers: getAuthHeader(),
    });
    const data = response.data; // { records, total, page, size, totalPages, eslesmeDurumSayilari } — 0-tabanlı
    return {
      total: data.total,
      page: data.page + 1,
      limit: data.size,
      records: (data.records || []).map(mapPersonnelRecord),
      eslesmeDurumSayilari: data.eslesmeDurumSayilari ?? null,
    };
  } catch (error) {
    console.error('Error fetching firma personel listesi:', error);
    parseError(error);
  }
}

/**
 * Tek bir personelin detayı (Personel Listesi'nin "Detaylar" satır aksiyonu).
 * Canlıda `fuarId` ZORUNLU, aynı zamanda firmaId+fuarId üyelik kontrolü olarak da
 * çalışır — bu firmanın bu fuardaki kadrosunda olmayan bir personId 404 döner
 * (bkz. rehber).
 * GET /api/v1/personnel/firma/{firmaId}/{personId}?fuarId=
 */
export async function getFirmaPersonelDetay(personelId, decoratorFirmaId, { fuarId, forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    const [personnel, firmaBelgeleri] = await Promise.all([
      getFirmaPersonnel(decoratorFirmaId),
      getFirmaBelgeleri(decoratorFirmaId),
    ]);
    const index = personnel.findIndex((p) => String(p.id) === String(personelId));
    if (index < 0) throw new Error('Personel bulunamadı');
    return enrichPersonel({ ...personnel[index], ...mockEslesme(personnel[index], index) }, firmaBelgeleri);
  }

  try {
    const response = await axios.get(
      `${JAVA_BACKEND_API_URL}/personnel/firma/${decoratorFirmaId}/${personelId}`,
      { params: { fuarId }, headers: getAuthHeader() },
    );
    return mapPersonnelRecord(response.data);
  } catch (error) {
    console.error('Error fetching firma personel detay:', error);
    parseError(error);
  }
}

/**
 * Firmanın Saka eşlemesine cevabı (backend #113) — Onayla (1) / Reddet (2, sebep zorunlu).
 * Mock dalında cevap yalnızca bu oturumun belleğinde tutulur; canlıda PATCH
 * /personnel/firma/{firmaId}/{personId}/eslesme-karari (bkz. personelRosterService.answerEslesme).
 */
export async function answerFirmaEslesme(
  { decoratorFirmaId, personId, fuarId, durum, sebep }, { forceLive = false } = {},
) {
  if (USE_MOCK_DATA && !forceLive) {
    mockEslesmeCevaplari.set(personId, {
      durum, sebep: durum === 2 ? String(sebep ?? '').trim() : null, tarih: nowStamp(),
    });
    return durum === 1 ? 'Eşleme onaylandı.' : 'Eşleme reddedildi; kişi firmanız üzerinden kart alamaz.';
  }
  return answerEslesme(decoratorFirmaId, personId, fuarId, { durum, sebep });
}
